import {
    RiDashboardLine, RiPencilLine, RiRestartLine, RiSearchLine,
} from "@remixicon/react";
import classNames from "classnames";
import React, {
    ReactElement,
    ReactNode,
    useMemo, useState,
} from "react";
import NoResults from "../agent-v3/board/no-results/NoResults";
import Confirm from "../confirm/Confirm";
import Button from "../theme/button/Button";
import FormGroup from "../theme/form-group/FormGroup";
import Styles from "./Template.module.scss";

export interface TemplateItem {
    id: string;
    title: string;
    description: string;
    categories: string[];
    icon?: ReactNode;
}

export interface TemplateViewProps {
    templates: TemplateItem[];
    categoryIcon?: ReactNode;
    onUseTemplate: (template: TemplateItem) => void;
    onCreate?: () => void;
    categoryIcons?: Record<string, ReactElement>
}

export function TemplateView({
    onCreate, templates, categoryIcon, onUseTemplate, categoryIcons,
}: TemplateViewProps) {
    const [selectedCategory, setSelectedCategory] = useState<string>();
    const [search, setSearch] = useState<string>();
    const categories = useMemo(() => templates.flatMap(template => template.categories).dedupe(), [templates]);

    const filteredItems = useMemo(() => {
        let items = templates;

        if (search) {
            const searchText = search.toLowerCase();
            items = items.filter(template => template.title?.toLowerCase().includes(searchText)
                || template.description?.toLowerCase().includes(searchText)
                || template.categories?.some(category => category.toLowerCase().includes(searchText)));
        }
        if (selectedCategory) {
            items = items.filter(template => template.categories.some(category => category === selectedCategory));
        }

        return items;
    }, [search, selectedCategory, templates]);

    return (
        <div className={Styles.container}>
            <div className={Styles.navigation}>
                <FormGroup
                    groupClassName={Styles.search}
                    placeholder="Search..."
                    size="small"
                    type="search"
                    prefixIcon={<RiSearchLine />}
                    onChange={event => setSearch(event.target.value)}
                    value={search}
                />
                {onCreate && (
                    <div className={Styles.create}>
                        <Button
                            size="small"
                            type="button"
                            variant="outline-gray"
                            prefixIcon={<RiPencilLine />}
                            onClick={onCreate}
                        >
                            Start from Scratch
                        </Button>
                    </div>
                )}
                <div className={Styles.categories}>
                    <h6 className={Styles["categories-title"]}>Templates</h6>
                    <div className={Styles["categories-list"]}>
                        <Button
                            type="button"
                            size="small"
                            variant="none"
                            className={classNames(Styles["category-button"], {
                                [Styles["category-button-selected"]]: selectedCategory == null,
                            })}
                            prefixIcon={categoryIcon}
                            onClick={() => setSelectedCategory(undefined)}
                        >
                            All
                        </Button>
                        {categories.map(category => (
                            <Button
                                key={category}
                                type="button"
                                size="small"
                                variant="none"
                                className={classNames(Styles["category-button"], {
                                    [Styles["category-button-selected"]]: selectedCategory === category,
                                })}
                                prefixIcon={categoryIcons?.[category] ?? categoryIcon}
                                onClick={() => setSelectedCategory(category)}
                            >
                                {category}
                            </Button>
                        ))}
                    </div>
                </div>
            </div>
            <div className={Styles.main}>
                {filteredItems.isEmpty && templates.isNotEmpty && (
                    <NoResults
                        title="No Results"
                        className={Styles["no-results"]}
                        text="Try adjusting your search to find more templates."
                        resetButtonText={search ? "Clear Search" : "Reset Filters"}
                        onClear={() => {
                            if (search) {
                                setSearch("");
                            } else {
                                setSelectedCategory(undefined);
                            }
                        }}
                        resetIcon={<RiRestartLine />}
                    />
                )}
                <div className={Styles["template-items"]}>
                    {filteredItems.map(item => (
                        <div key={item.id} className={Styles["template-item"]}>
                            <div className={Styles["template-icon"]}>
                                {item.icon ?? categoryIcon}
                            </div>
                            <h6 className={Styles["template-title"]}>{item.title}</h6>
                            <p className={Styles["template-description"]}>{item.description}</p>
                            <div className={Styles.actions}>
                                <Button
                                    size="small"
                                    variant="outline-gray"
                                    onClick={() => onUseTemplate(item)}
                                >
                                    Use Template
                                </Button>
                            </div>

                        </div>
                    ))}
                </div>

            </div>
        </div>
    );
}

TemplateView.defaultProps = {
    onCreate: undefined,
    categoryIcon: <RiDashboardLine />,
    categoryIcons: {},
};

export interface TemplateProps extends TemplateViewProps {
    shouldShow: boolean;
    title: string;
    helpArticleId?: string;
    helpText?: string;
    onHide?: () => void;
    categoryIcons?: Record<string, ReactElement>
}

export default function Template(props: TemplateProps) {
    const {
        shouldShow, onHide, title, helpArticleId, helpText, ...rest
    } = props;

    return (
        <Confirm
            bodyClassName={Styles.modal}
            className={Styles["modal-dialog"]}
            size="xl"
            shouldShowFooter={false}
            shouldShow={shouldShow}
            onHide={onHide}
            title={title}
            helpArticleId={helpArticleId}
            helpText={helpText}
        >
            <TemplateView {...rest} />
        </Confirm>
    );
}

Template.defaultProps = {
    onCreate: undefined,
    categoryIcon: <RiDashboardLine />,
    helpArticleId: undefined,
    helpText: undefined,
    categoryIcons: {},
    onHide: undefined,
};

export function useTemplate(props: Omit<TemplateProps, "shouldShow">) {
    const [isShow, setIsShow] = useState(false);

    return {
        element: <Template
            {...props}
            shouldShow={isShow}
            onHide={() => {
                setIsShow(false);
            }}
        />,
        onShow: () => setIsShow(true),
        onHide: () => setIsShow(false),
    };
}
